import {
  Outlet,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'

import { createSerializer, parseAsString } from 'nuqs'

import { BufferTrackerReact } from '@buffer-mono/tracking-plan'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { useOnce } from '~publish/hooks/useOnce'
import { createTemplates } from '~publish/legacy/routes'

import { TemplateDialog } from './TemplateDialog'
import { useTemplateLibrary } from '../../campaigns/TemplateLibraryContext'
import { useMyPostTemplates } from '../../hooks/useMyPostTemplates'

import type { JSX } from 'react'

// Dialog-only params the route owns. Driving the close-side strip through
// a nuqs serializer keeps list-level state (view, query, filters) intact
// regardless of which keys the library page happens to track.
const serializeListSearch = createSerializer({
  cta: parseAsString,
  trigger: parseAsString,
  preselectChannelId: parseAsString,
})

const isCta = (cta: string | null | undefined): cta is CTA => {
  if (!cta) {
    return false
  }
  return (
    (cta.startsWith('create-') ||
      cta.startsWith('publish-') ||
      cta.startsWith('community-')) &&
    cta.split('-').length === 5 &&
    cta[cta.length - 1].match(/^\d+$/) !== null
  )
}

type TemplateDialogParams = {
  templateId: string
}

export const TemplateDialogRoute = (): JSX.Element | null => {
  const navigate = useNavigate()
  const { templateId } = useParams<TemplateDialogParams>()
  const [searchParams] = useSearchParams()
  const ctaParam = searchParams.get('cta')
  const cta = isCta(ctaParam) ? ctaParam : 'create-templates-dialog-deepLink-1'
  const preselectChannelId = searchParams.get('preselectChannelId') ?? undefined
  const triggerId = searchParams.get('trigger') ?? undefined

  // Use unfiltered templates so routes are still available even if they don't
  // show in the list (e.g. filtered out by campaign rules) — preserves shared links.
  const { templates, loading: publicLoading } = useTemplateLibrary()
  const publicMatch = templates.find((t) => t.id === templateId)

  // Only reach for the actor's custom templates once the public list has
  // resolved without a match. This keeps the deep-link path for
  // Buffer-curated templates at a single query.
  const shouldLookupCustom = !publicMatch && !publicLoading
  const { templates: myCustomTemplates } = useMyPostTemplates({
    skip: !shouldLookupCustom,
  })
  // When skipped, `myCustomTemplates` is the empty array, so this `find`
  // safely returns `undefined` without a separate gate.
  const customMatch = myCustomTemplates.find((t) => t.id === templateId)
  const template = publicMatch ?? customMatch
  const isCustom = Boolean(customMatch)

  const organizationId = useOrganizationId()

  useOnce(() => {
    if (templateId && organizationId) {
      BufferTrackerReact.templateClicked({
        cta,
        organizationId: organizationId ?? '',
        product: 'publish',
        templateId,
      })
      return true
    }
    return false
  }, [organizationId, templateId, cta])

  const onClose = (open: boolean): void => {
    if (!open) {
      // Preserve list-level URL state (e.g. ?view=mine) when closing the
      // dialog so the user lands back on the tab they came from. Drop
      // dialog-only params (cta, trigger, preselectChannelId) by passing
      // null through the nuqs serializer.
      const search = serializeListSearch(searchParams, {
        cta: null,
        trigger: null,
        preselectChannelId: null,
      })
      navigate({
        pathname: createTemplates.route,
        search,
      })
    }
  }

  if (!template) {
    return null
  }

  return (
    <>
      <Outlet />
      <TemplateDialog
        template={template}
        cta={cta}
        preselectChannelId={preselectChannelId}
        triggerId={triggerId}
        isCustom={isCustom}
        open={true}
        onOpenChange={onClose}
      />
    </>
  )
}
